import { RowActions } from "@/shared/ui/RowActions";
import { Card, CardContent, confirm, Loader } from "@devhop/ui";
import { EditIcon, Shield, TrashIcon } from "lucide-react";
import type { ShopRole } from "../api/client";

type ShopRolesTableProps = {
  data: ShopRole[];
  isLoading?: boolean;
  onEdit: (role: ShopRole) => void;
  onDelete: (role: ShopRole) => void;
  canUpdate: boolean;
  canDelete: boolean;
};

const NO_DATA_MSG = "ຍັງບໍ່ມີບົດບາດ. ກົດ ສ້າງຄ່າເລີ່ມຕົ້ນ ຫຼື ເພີ່ມບົດບາດ ເພື່ອເລີ່ມຕົ້ນ.";

export function ShopRolesTable({
  data,
  isLoading,
  onEdit,
  onDelete,
  canUpdate,
  canDelete,
}: ShopRolesTableProps) {
  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-xl border bg-card p-8">
        <Loader />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-muted/20 p-8 text-center">
        <Shield className="size-10 text-muted-foreground/60" />
        <p className="text-muted-foreground text-sm">{NO_DATA_MSG}</p>
      </div>
    );
  }

  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {data.map((role) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];
        if (canUpdate)
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="size-4" />,
            onClick: () => onEdit(role),
          });
        if (canDelete)
          actions.push({
            label: "ລົບ",
            variant: "destructive",
            icon: <TrashIcon className="size-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລົບບົດບາດ",
                description: `ຕ້ອງການລົບ "${role.name}" ຫຼືບໍ່? ພະນັກງານທີ່ໃຊ້ບົດບາດນີ້ອາດຖືກປ່ຽນສະຖານະ.`,
                actionText: "ລົບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) onDelete(role);
            },
          });

        return (
          <li key={role.id}>
            <Card className="flex flex-col gap-3">
              <CardContent>
                <div className="flex min-w-0 items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-foreground">
                      {role.name}
                    </p>
                    <p className="mt-0.5 text-muted-foreground text-sm">
                      {role.permissions.length} ລາຍການສິດ
                    </p>
                  </div>
                </div>
                {actions.length > 0 && (
                  <div className="mt-auto flex flex-wrap gap-2 border-t pt-3">
                    <RowActions actions={actions} maxInline={2} />
                  </div>
                )}
              </CardContent>
            </Card>
          </li>
        );
      })}
    </ul>
  );
}
